import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { LeaderRow, SlideKind, SlidePhase } from '../lib/types'
import { applyTheme, nextTheme, readTheme, THEME_LABEL, type Theme } from '../lib/theme'

export function Brand({ to = '/' }: { to?: string }) {
  return (
    <Link className="brand" to={to}>
      clap<span>me</span>
    </Link>
  )
}

/**
 * Переключатель темы: система → светлая → тёмная → система.
 *
 * Три состояния, а не два: «как в системе» — это не то же самое, что выбранная
 * вручную тёмная. Человек, у которого телефон сам переключается к вечеру,
 * не должен терять это поведение, один раз нажав на кнопку.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => readTheme())

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const icon: Record<Theme, string> = { system: '◐', light: '☀', dark: '☾' }

  return (
    <button
      className="theme-toggle"
      onClick={() => setTheme((current) => nextTheme(current))}
      title={`Тема: ${THEME_LABEL[theme]}`}
      aria-label={`Тема: ${THEME_LABEL[theme]}. Переключить`}
      data-testid="theme"
      data-theme-choice={theme}
    >
      {icon[theme]}
    </button>
  )
}

/** Индикатор прямого эфира. Нужен на проекторе: зал должен видеть, что всё живое. */
export function LiveDot({ label = 'В эфире' }: { label?: string }) {
  return <span className="live-dot">{label}</span>
}

export function PhaseBadge({ phase, kind }: { phase: SlidePhase; kind?: SlideKind }) {
  // «Идёт голосование» на слайде с эмодзи звучит мимо: там не голосуют,
  // а реагируют. Подпись открытой фазы зависит от того, что слайд просит сделать.
  const open =
    kind === 'EMOJI_STORM' ? 'реакции открыты' : kind === 'FREE_TEXT' ? 'ждём ответы' : 'идёт голосование'

  const label: Record<SlidePhase, string> = {
    IDLE: 'не начат',
    OPEN: open,
    CLOSED: 'закрыт',
    REVEALED: 'результат показан',
  }
  const cls = phase === 'OPEN' ? 'badge badge-open' : phase === 'REVEALED' ? 'badge badge-live' : 'badge'
  return <span className={cls}>{label[phase]}</span>
}

/**
 * Число, которое набегает до нового значения вместо мгновенной подмены.
 *
 * На проекторе это не украшение: счётчик реакций меняется несколько раз в секунду,
 * и скачки цифр читаются как помеха, а плавный набег — как живой зал.
 */
export function useRolling(value: number, durationMs = 450): number {
  const [shown, setShown] = useState(value)
  const from = useRef(value)
  const raf = useRef<number>(0)

  useEffect(() => {
    const start = performance.now()
    const begin = from.current
    const delta = value - begin

    if (delta === 0) return

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      // easeOutCubic: быстро стартует, мягко доезжает.
      const eased = 1 - (1 - t) ** 3
      setShown(Math.round(begin + delta * eased))
      if (t < 1) {
        raf.current = requestAnimationFrame(step)
      } else {
        from.current = value
      }
    }

    raf.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf.current)
  }, [value, durationMs])

  return shown
}

export function Counter({ value, className }: { value: number; className?: string }) {
  return <span className={className}>{useRolling(value)}</span>
}

const RING_RADIUS = 36
const RING_LENGTH = 2 * Math.PI * RING_RADIUS

/** Обратный отсчёт кольцом: круг читается с дальнего ряда лучше, чем полоска. */
export function Timer({ msLeft, totalSeconds }: { msLeft: number | null; totalSeconds: number | null }) {
  if (msLeft == null) return null

  const seconds = Math.ceil(msLeft / 1000)
  const share = totalSeconds ? Math.max(0, Math.min(1, msLeft / (totalSeconds * 1000))) : 0
  const hot = seconds <= 5

  return (
    <div className={`timer-ring${hot ? ' hot' : ''}`} role="timer" aria-live="off">
      <svg viewBox="0 0 84 84" aria-hidden>
        <circle className="track" cx="42" cy="42" r={RING_RADIUS} />
        <circle
          className="value"
          cx="42"
          cy="42"
          r={RING_RADIUS}
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - share)}
        />
      </svg>
      <span className="timer">{seconds}&nbsp;с</span>
    </div>
  )
}

/**
 * Таблица лидеров.
 *
 * `limit` и `meId` — для телефона: участнику незачем листать весь зал, ему
 * нужны первые места и он сам. Если он не в числе первых, его строка идёт
 * отдельно, после отбивки, со своим настоящим местом.
 */
export function Leaders({
  rows,
  title = 'Лидеры',
  limit,
  meId,
}: {
  rows: LeaderRow[]
  title?: string
  limit?: number
  meId?: string | null
}) {
  if (rows.length === 0) return null

  const top = limit ? rows.slice(0, limit) : rows
  const myIndex = meId ? rows.findIndex((row) => row.participantId === meId) : -1
  const mineBelow = limit != null && myIndex >= limit ? rows[myIndex] : null

  const line = (row: LeaderRow, index: number) => (
    <div
      className={`leader${index === 0 ? ' leader-1' : ''}${row.participantId === meId ? ' leader-me' : ''}`}
      key={row.participantId}
    >
      {/* Место цифрой на плитке, как номер на табло: цвет металла даёт CSS. */}
      <span className="leader-place">{index + 1}</span>
      <span>
        {row.name}
        {row.participantId === meId && <span className="muted"> · вы</span>}
      </span>
      <span className="leader-score">{row.score}</span>
    </div>
  )

  return (
    <div className="col">
      <h3 className="eyebrow">{title}</h3>
      <div className="leaders">
        {top.map(line)}
        {mineBelow && (
          <>
            <span className="leader-gap" aria-hidden>
              ···
            </span>
            {line(mineBelow, myIndex)}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Подиум для итогов на проекторе. Порядок колонок — 2, 1, 3, как на настоящем:
 * победитель в центре и выше остальных.
 */
export function Podium({ rows }: { rows: LeaderRow[] }) {
  if (rows.length === 0) return null

  const top = rows.slice(0, 3)
  const order = [1, 0, 2].filter((index) => index < top.length)

  return (
    // Колонок ровно столько, сколько призёров: иначе один победитель уезжал
    // в левую треть экрана вместо центра.
    <div className="podium" style={{ gridTemplateColumns: `repeat(${top.length}, minmax(0, 1fr))` }}>
      {order.map((index) => (
        <div className={`podium-step podium-step-${index + 1}`} key={top[index].participantId}>
          <span className="podium-medal">{index + 1}</span>
          <span className="podium-name">{top[index].name}</span>
          <span className="podium-score">{top[index].score}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Точки пройденного пути.
 *
 * У участника раньше не было никакого ощущения хода эфира: где он, сколько
 * осталось. На проекторе «слайд 2 из 3» есть, а на телефоне не было ничего.
 */
export function Progress({ index, total }: { index: number; total: number }) {
  if (total <= 1) return null

  return (
    <div className="progress" role="group" aria-label={`Слайд ${index + 1} из ${total}`}>
      {Array.from({ length: total }, (_, i) => (
        <i key={i} className={i === index ? 'progress-now' : i < index ? 'progress-done' : ''} />
      ))}
    </div>
  )
}

/**
 * Невидимая область для скринридера.
 *
 * Фазы эфира меняет ведущий, а не участник: без объявления человек со
 * скринридером просто не узнает, что голосование открылось или закрылось.
 */
export function Announce({ children }: { children: string }) {
  return (
    <span aria-live="polite" role="status" className="sr-only">
      {children}
    </span>
  )
}

export function Toast({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <div className="toast" role="status">
      {message}
    </div>
  )
}

export function Offline({ reconnecting }: { reconnecting: boolean }) {
  if (!reconnecting) return null
  return <div className="offline">Связь потеряна — переподключаемся…</div>
}
